import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  serviceAgentIntroductionService,
  IntroduceInvestorLookups,
  IntroduceInvestorRequest,
} from '../../../services/serviceAgentIntroduction.service';
import { IntroduceEmailCheck, IntroducePrefill } from '../../../models/ServiceAgentIntroductionDto';
import { IntroOption } from '../../../models/ServiceAgentIntroductionDto';
import '../../../components/LoadingSpinner/LoadingSpinner.scss';
import { SearchableSelect, SearchableOption } from '../../../components/SearchableSelect/SearchableSelect';
import { getPermissionErrorMessage } from '../../../utils/apiClient';
import './IntroduceInvestor.scss';

type FormState = {
  investorTypeGuid: string;
  nationalityGuid: string;
  countryOfResidenceGuid: string;
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  isdCodeGuid: string;
  mobile: string;
  useFacilonServiceAgent: boolean;
  facilonServiceAgentGuid: string;
  investmentRouteGuid: string;
  productGuid: string;
  brokerBankGuid: string;
  planGuid: string;
};

const EMPTY: FormState = {
  investorTypeGuid: '', nationalityGuid: '', countryOfResidenceGuid: '',
  firstName: '', middleName: '', lastName: '', email: '', isdCodeGuid: '', mobile: '',
  useFacilonServiceAgent: false, facilonServiceAgentGuid: '',
  investmentRouteGuid: '', productGuid: '', brokerBankGuid: '', planGuid: '',
};

/**
 * Introduce Investor — the Client Executive Agent's version of the Power Apps
 * "Broker – Investor (Individual) → Create" form.
 *
 * The service provider and the executive agent are shown read-only because the API takes them
 * from the signed-in profile; nothing about them is submitted from here.
 */
export const IntroduceInvestor: React.FC = () => {
  const navigate = useNavigate();
  const [lookups, setLookups] = useState<IntroduceInvestorLookups | null>(null);
  // Which of the agent's service providers this introduction is for (agents may act for several).
  const [firmId, setFirmId] = useState<string | null>(null);
  const [switchingFirm, setSwitchingFirm] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [blocked, setBlocked] = useState<string | null>(null);
  const [emailCheck, setEmailCheck] = useState<IntroduceEmailCheck | null>(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  // What the last self-registered prefill put into the form, so a changed email can take it back.
  const [prefilled, setPrefilled] = useState<{ email: string; values: Partial<FormState> } | null>(null);
  // The ISD code this form chose from the country of residence; null when the agent picked it.
  const autoIsd = useRef<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await serviceAgentIntroductionService.getLookups();
        setLookups(data);
        setFirmId(data.defaultFirmId ?? data.firms?.[0]?.firmId ?? null);
        setForm(f => ({ ...f, investmentRouteGuid: defaultRouteFor(data) }));
      } catch (err: any) {
        const permissionError = getPermissionErrorMessage(err);
        const message = err?.response?.data?.message;
        if (err?.response?.status === 409 && message) {
          setBlocked(message);
        } else if (permissionError) {
          toast.error(permissionError);
        } else {
          toast.error(message || 'Failed to load the form');
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /**
   * The portal's default route by firm type: "PMS" for a portfolio manager, "Direct" otherwise
   * (verified against portal-created INV-1845 vs ours, 2026-10-09). Blank when the list lacks it.
   */
  const defaultRouteFor = (data: IntroduceInvestorLookups): string => {
    const want = data.spType === 100000003 ? 'pms' : 'direct';
    return data.investmentRoutes.find(r => r.name?.trim().toLowerCase() === want)?.id || '';
  };

  /*
   * Switching firm reloads the firm-scoped lists (products, plans, banks) and drops the choices made
   * from the old lists; the investor's identity fields and the email stay. The email check re-runs on
   * its own because it depends on firmId (duplicate-product is judged per firm).
   */
  const changeFirm = async (nextFirmId: string) => {
    if (!nextFirmId || nextFirmId === firmId) return;
    setSwitchingFirm(true);
    try {
      const data = await serviceAgentIntroductionService.getLookups(nextFirmId);
      setLookups(data);
      setFirmId(nextFirmId);
      setForm(f => ({ ...f, productGuid: '', planGuid: '', brokerBankGuid: '', investmentRouteGuid: defaultRouteFor(data) }));
      setErrors(e => ({ ...e, productGuid: '', planGuid: '', brokerBankGuid: '', investmentRouteGuid: '' }));
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not switch service provider');
    } finally {
      setSwitchingFirm(false);
    }
  };

  const set = (k: keyof FormState, v: string | boolean) => {
    setForm(f => ({ ...f, [k]: v } as FormState));
    setErrors(e => (e[k] ? { ...e, [k]: '' } : e));
  };

  /**
   * Map a self-registered account's facts onto this form's option lists. Only options that exist
   * here are used, so a hidden type (Resident Individual) or an unmatched country stays blank for
   * the agent to pick.
   */
  const toPrefillValues = (p: IntroducePrefill): Partial<FormState> => {
    const v: Partial<FormState> = {};
    if (p.firstName) v.firstName = p.firstName;
    if (p.middleName) v.middleName = p.middleName;
    if (p.lastName) v.lastName = p.lastName;
    if (p.nationalityGuid && lookups?.nationalities.some(o => o.id === p.nationalityGuid)) {
      v.nationalityGuid = p.nationalityGuid;
    }
    if (p.investorTypeCode) {
      const type = investorTypes.find(t => (t.parentId || '').trim() === p.investorTypeCode);
      if (type) v.investorTypeGuid = type.id;
    }
    if (p.countryOfResidenceName) {
      const want = p.countryOfResidenceName.trim().toLowerCase();
      const country = lookups?.countriesOfResidence.find(o => o.name.trim().toLowerCase() === want);
      if (country) v.countryOfResidenceGuid = country.id;
    }
    if (p.isdCode) {
      const dial = p.isdCode.replace(/\s+/g, '');
      const isd = lookups?.isdCodes.find(o => o.name.replace(/\s+/g, '').startsWith(dial + ''));
      if (isd) v.isdCodeGuid = isd.id;
    }
    if (p.mobile) v.mobile = p.mobile.replace(/[^0-9]/g, '');
    return v;
  };

  /** Fill only the fields still blank, so nothing the agent typed is overwritten. */
  const applyPrefill = (email: string, p: IntroducePrefill) => {
    const values = toPrefillValues(p);
    setForm(f => {
      const next = { ...f };
      (Object.keys(values) as (keyof FormState)[]).forEach(k => {
        if (!String(f[k] ?? '').trim()) (next as any)[k] = values[k];
      });
      return next;
    });
    setErrors(e => {
      const next = { ...e };
      Object.keys(values).forEach(k => { if (next[k]) next[k] = ''; });
      return next;
    });
    setPrefilled({ email, values });
  };

  /** Take back prefilled values the agent has not changed since; called when the email moves on. */
  const revertPrefill = () => {
    if (!prefilled) return;
    const { values } = prefilled;
    setForm(f => {
      const next = { ...f };
      (Object.keys(values) as (keyof FormState)[]).forEach(k => {
        if (f[k] === values[k]) (next as any)[k] = EMPTY[k];
      });
      return next;
    });
    setPrefilled(null);
  };

  /*
   * Tell the agent what a known email means, before they submit. Advisory only: introducing an
   * existing investor is how they are offered a second product, and the API allows it. Debounced
   * so it fires once the address stops changing rather than on every keystroke.
   *
   * A self-registered, never-introduced account also comes back with the investor's registration
   * details, which are filled into the blank fields (client request 2026-10-08).
   */
  useEffect(() => {
    const email = form.email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailCheck(null);
      setCheckingEmail(false);
      if (prefilled && prefilled.email !== email) revertPrefill();
      return;
    }
    let cancelled = false;
    setCheckingEmail(true);
    const timer = window.setTimeout(async () => {
      try {
        const result = await serviceAgentIntroductionService.checkEmail(email, form.productGuid, firmId);
        if (cancelled) return;
        setEmailCheck(result.status === 'NEW' ? null : result);
        if (result.prefill) {
          if (!prefilled || prefilled.email !== email) applyPrefill(email, result.prefill);
        } else if (prefilled && prefilled.email !== email) {
          revertPrefill();
        }
      } catch {
        // A failed advisory check must never get in the way of the form.
        if (!cancelled) setEmailCheck(null);
      } finally {
        if (!cancelled) setCheckingEmail(false);
      }
    }, 500);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [form.email, form.productGuid, firmId]); // eslint-disable-line react-hooks/exhaustive-deps

  /*
   * Resident Individual is hidden from the list for now (business call, 2026-09-28) — the
   * option still exists in Dataverse and the RI defaults below are kept, so restoring it is
   * a matter of deleting this filter.
   */
  const investorTypes: IntroOption[] = useMemo(
    () => (lookups?.investorTypes || []).filter(
      t => t.parentId !== '100000000' && !/^resident individual$/i.test(t.name.trim())),
    [lookups],
  );

  // PMS firms: the plan carries the scheme and the preferred bank, so the bank field is not asked.
  const isPms = lookups?.spType === 100000003;
  const planDetail = useMemo(
    () => (lookups?.planDetails || []).find(d => d.planGuid === form.planGuid) || null,
    [lookups, form.planGuid],
  );

  const selectedType = useMemo(
    () => lookups?.investorTypes.find(t => t.id === form.investorTypeGuid) || null,
    [lookups, form.investorTypeGuid],
  );

  /*
   * Investor-type defaults, filled only into fields still blank so nothing chosen is
   * overwritten, and every field stays editable:
   *   Resident Individual - Indian, living in India, +91.
   *   NRI                 - Indian by definition; residence and ISD are theirs to pick.
   * Matched by name because the GUIDs differ per Dataverse environment.
   */
  useEffect(() => {
    if (!lookups || !selectedType) return;
    const typeName = selectedType.name.trim();
    const isResident = selectedType.parentId === '100000000' || /^resident individual$/i.test(typeName);
    const isNri = selectedType.parentId === '100000003' || /^nri$/i.test(typeName);
    if (!isResident && !isNri) return;
    const find = (list: IntroOption[], test: (o: IntroOption) => boolean) =>
      list.find(test)?.id || '';
    const nationality = find(lookups.nationalities,
      o => /^indian$/i.test(o.name.trim()) || (o.parentId || '').trim().toUpperCase() === 'IN');
    const residence = find(lookups.countriesOfResidence, o => /^india$/i.test(o.name.trim()));
    const isd = find(lookups.isdCodes, o => /^\+?91\s+india$/i.test(o.name.trim()));
    setForm(f => ({
      ...f,
      nationalityGuid: f.nationalityGuid || nationality,
      countryOfResidenceGuid: f.countryOfResidenceGuid || (isResident ? residence : ''),
      isdCodeGuid: f.isdCodeGuid || (isResident ? isd : ''),
    }));
    setErrors(e => (e.nationalityGuid && nationality ? { ...e, nationalityGuid: '' } : e));
  }, [selectedType, lookups]);

  /*
   * ISD code follows the country of residence (user 2026-10-09): the ISD list is labelled
   * "+65 Singapore", so the country name picks it. Set only while the ISD is blank or was chosen by
   * this rule — an agent's own choice is never overwritten — and left blank when no label matches.
   */
  useEffect(() => {
    if (!lookups) return;
    const country = lookups.countriesOfResidence.find(o => o.id === form.countryOfResidenceGuid);
    const agentChoseIsd = form.isdCodeGuid && form.isdCodeGuid !== autoIsd.current;
    if (agentChoseIsd) return;
    if (!country) {
      if (autoIsd.current) { autoIsd.current = null; setForm(f => ({ ...f, isdCodeGuid: '' })); }
      return;
    }
    const want = country.name.trim().toLowerCase();
    const match = lookups.isdCodes.find(o => {
      const label = o.name.trim().toLowerCase();
      return label.endsWith(' ' + want) || label === want;
    });
    const next = match?.id || '';
    if (next !== form.isdCodeGuid) {
      autoIsd.current = next || null;
      setForm(f => ({ ...f, isdCodeGuid: next }));
    }
  }, [form.countryOfResidenceGuid, lookups]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Products carry `ss_applicableto` (a "; " separated list of investor-type names) in parentId.
   * Until a type is chosen, show everything the provider has a plan for.
   */
  const products: IntroOption[] = useMemo(() => {
    const all = lookups?.products || [];
    // Both sides are Dataverse option CODES, not labels: a product's parentId is its raw
    // ss_applicableto ("100000003,100000004") and the investor type's is its own code.
    const wanted = selectedType?.parentId?.trim();
    if (!wanted) return all;
    const matching = all.filter(p =>
      (p.parentId || '')
        .split(/[,;]/)
        .map(v => v.trim())
        .filter(Boolean)
        .includes(wanted),
    );
    // A product with no applicability recorded should not disappear from the list.
    const unscoped = all.filter(p => !p.parentId || !p.parentId.trim());
    return [...matching, ...unscoped];
  }, [lookups, selectedType]);

  // Every plan the firm offers, whatever the product: the Dataverse lookup is not narrowed by
  // product, so narrowing it here would hide plans the portal would let you pick.
  const plans: IntroOption[] = useMemo(() => lookups?.plans || [], [lookups]);

  // Clear dependent choices when their parent changes, so a stale pair can never be submitted.
  useEffect(() => {
    if (form.productGuid && !products.some(p => p.id === form.productGuid)) {
      setForm(f => ({ ...f, productGuid: '', planGuid: '' }));
    }
  }, [products]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (form.planGuid && !plans.some(p => p.id === form.planGuid)) {
      setForm(f => ({ ...f, planGuid: '' }));
    }
  }, [plans]); // eslint-disable-line react-hooks/exhaustive-deps

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!form.investorTypeGuid) e.investorTypeGuid = 'Select an investor type';
    if (!form.nationalityGuid) e.nationalityGuid = 'Select a nationality';
    if (!form.firstName.trim()) e.firstName = 'First name is required';
    if (!form.lastName.trim()) e.lastName = 'Last name is required';
    if (!form.email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = 'Enter a valid email address';
    if (!form.mobile.trim()) e.mobile = 'Mobile number is required';
    else if (!/^[0-9]{6,15}$/.test(form.mobile.trim())) e.mobile = 'Enter 6 to 15 digits';
    if (!form.investmentRouteGuid) e.investmentRouteGuid = 'Select an investment route';
    if (!form.productGuid) e.productGuid = 'Select a product';
    if (!isPms && !form.brokerBankGuid) e.brokerBankGuid = 'Select a preferred bank';
    if (!form.planGuid) e.planGuid = isPms ? 'Select a PMS plan' : 'Select a brokerage plan';
    if (form.useFacilonServiceAgent && !form.facilonServiceAgentGuid) {
      e.facilonServiceAgentGuid = 'Choose a service agent, or set this to No';
    }
    // The server enforces this too; stopping here saves a round trip and points at the field.
    if (emailCheck?.status === 'DUPLICATE_PRODUCT') {
      e.productGuid = 'This investor already has this product';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error('Please correct the highlighted fields');
      return;
    }
    setSaving(true);
    try {
      const payload: IntroduceInvestorRequest = {
        investorTypeGuid: form.investorTypeGuid,
        nationalityGuid: form.nationalityGuid,
        countryOfResidenceGuid: form.countryOfResidenceGuid || null,
        firstName: form.firstName.trim(),
        middleName: form.middleName.trim() || null,
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        isdCodeGuid: form.isdCodeGuid || null,
        mobile: form.mobile.trim(),
        useFacilonServiceAgent: form.useFacilonServiceAgent,
        facilonServiceAgentGuid: form.useFacilonServiceAgent ? form.facilonServiceAgentGuid : null,
        investmentRouteGuid: form.investmentRouteGuid,
        productGuid: form.productGuid,
        brokerBankGuid: isPms ? '' : form.brokerBankGuid,
        planGuid: form.planGuid,
        firmId,
      };
      const created = await serviceAgentIntroductionService.introduce(payload);
      toast.success(`${created.investorName} introduced as ${created.code}`);
      navigate('/service-agent/introduced-investors', { state: { justCreated: created.id } });
    } catch (err: any) {
      const message = err?.response?.data?.message;
      toast.error(message || 'The investor could not be introduced. Nothing was saved.');
    } finally {
      setSaving(false);
    }
  };


  if (!loading && blocked) {
    return (
      <div className="facilon-dashboard-wrapper font-sans text-gray-800 min-h-screen">
        <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] p-4 max-w-2xl">
          <h2 className="text-[15px] font-bold text-slate-800 mb-2">
            <i className="bi bi-exclamation-triangle text-[#b45309] mr-2"></i>
            Introductions are not available for your profile
          </h2>
          <p className="text-[12px] text-slate-600 mb-3">{blocked}</p>
          <button className="btn btn-sm btn-secondary" onClick={() => navigate('/service-agent/dashboard')}>
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  /** Only nationalities put a searchable code in parentId; elsewhere it is a grouping key. */
  const asOptions = (list: IntroOption[] | undefined, withKeywords = false): SearchableOption[] =>
    (list || []).map(o => ({ id: o.id, name: o.name, keywords: withKeywords ? o.parentId : null }));

  const err = (k: string) => (errors[k] ? <div className="invalid-feedback d-block text-[11px]">{errors[k]}</div> : null);
  const cls = (k: string) => `form-control form-control-sm${errors[k] ? ' is-invalid' : ''}`;

  /*
   * Identity facts that came from the investor's own registration are shown read-only: they are
   * the investor's to change (in their profile), and retyping the name here would make the linked
   * journey look like a different person's. Fields the prefill left blank stay editable, as do the
   * phone and product fields. Changing the email unlocks everything again.
   */
  const LOCKABLE: (keyof FormState)[] = [
    'firstName', 'middleName', 'lastName', 'nationalityGuid', 'countryOfResidenceGuid', 'investorTypeGuid',
  ];
  const isLocked = (k: keyof FormState) =>
    !!prefilled && LOCKABLE.includes(k) && prefilled.values[k] !== undefined && form[k] === prefilled.values[k];
  const lockStyle = { backgroundColor: '#e9ecef', color: '#495057', cursor: 'not-allowed' } as const;
  const lockHint = (k: keyof FormState) =>
    isLocked(k) ? <div className="form-text text-[11px]">From the investor's registration.</div> : null;

  return (
    <div className="sa-introduce facilon-dashboard-wrapper font-sans text-gray-800 min-h-screen pb-0">
      {/* Centred logo loader over a translucent backdrop: the form stays visible (and mounted,
          with its values intact if Power Apps rejects the write) — only the logo and ring
          animate in the middle, rather than the whole page turning teal. */}
      {saving && (
        <div className="sa-introduce__saving" role="status" aria-live="polite">
          <div className="loading-brand">
            <div className="loading-ring"></div>
            <img className="loading-logo" src="/assets/images/logo.png" alt="" />
          </div>
          <p>Creating the investor in Power Apps…</p>
        </div>
      )}
      <main className="container-fluid px-0 pt-0 pb-0 dashboard-container-main">

        {/* Banner */}
        <div className="bg-gradient-to-r from-[#2c5e6a] to-[#355f69] text-white rounded shadow-sm mb-2 px-4 py-2.5 flex justify-between items-center flex-wrap gap-2">
          <div>
            <h1 className="text-[16px] font-bold text-white mb-0">Introduce Investor</h1>
            <p className="text-[12px] opacity-90 mt-1 mb-0">
              Create an introduction for your service provider. The investor is emailed a registration link.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/service-agent/introduced-investors')}
            className="bg-white/10 border border-white/25 rounded px-2.5 py-1 text-[11px] cursor-pointer hover:bg-white/20 transition-colors"
          >
            My Introductions
          </button>
        </div>

        {loading ? (
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] mb-2"><div className="py-5 text-center"><Spinner animation="border" variant="primary" /></div></div>
        ) : (<>
        {/* Who this is being created for — resolved server-side, shown so the agent can check it */}
        <div className="bg-[#f1f7f8] border border-[#cfe0e4] rounded-lg px-3 py-2 mb-2 flex flex-wrap items-center gap-x-7 gap-y-1">
          <div>
            <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Introducing on behalf of</div>
            {(lookups?.firms?.length ?? 0) > 1 ? (
              <select
                id="firm"
                className="form-select form-select-sm mt-1"
                style={{ minWidth: 280 }}
                value={firmId ?? ''}
                disabled={switchingFirm || saving}
                aria-label="Service provider to introduce for"
                onChange={e => changeFirm(e.target.value)}
              >
                {lookups!.firms.map(f => (
                  <option key={f.firmId} value={f.firmId}>
                    {f.name}{f.code ? ` (${f.code})` : ''}{f.spTypeLabel ? ` — ${f.spTypeLabel}` : ''}
                  </option>
                ))}
              </select>
            ) : (
              <div className="text-[12px] font-semibold text-slate-800">
                {lookups?.serviceProviderName}
                {lookups?.serviceProviderCode && (
                  <span className="text-[11px] text-slate-500 font-normal ml-2">{lookups.serviceProviderCode}</span>
                )}
              </div>
            )}
          </div>
          <div>
            <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Client Executive Agent</div>
            <div className="text-[12px] font-semibold text-slate-800">
              {lookups?.clientExecutiveAgentName}
              <span className="text-[11px] text-slate-500 font-normal ml-2">(you)</span>
            </div>
          </div>
          <div className="text-[10.5px] text-slate-500 ml-auto max-w-[240px] leading-snug">
            {(lookups?.firms?.length ?? 0) > 1
              ? 'You act for more than one service provider: pick the one this introduction is for.'
              : 'Both come from your agent profile and are applied automatically.'}
          </div>
        </div>

        <form onSubmit={submit} noValidate>

          {/* Investor Email — first, because a self-registered account pre-fills the rest */}
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] mb-2">
            <div className="sa-introduce__section-head">
              <span>Investor Email</span>
            </div>
            <div className="sa-introduce__grid">
              <div className="form-group form-group--wide">
                <label htmlFor="email">
                  Email <span className="text-danger">*</span>
                </label>
                <input id="email" maxLength={190} type="email" className={cls('email')} value={form.email}
                       onChange={e => set('email', e.target.value)} />
                {err('email')}
                {!emailCheck && (
                  <div className="form-text text-[11px]">
                    {checkingEmail ? 'Checking this email…' : 'The registration link is sent here.'}
                  </div>
                )}
                {emailCheck && (
                  <div
                    className={`sa-introduce__notice${emailCheck.status === 'DUPLICATE_PRODUCT' ? ' is-blocking' : ''}`}
                    role={emailCheck.status === 'DUPLICATE_PRODUCT' ? 'alert' : 'status'}
                  >
                    <i
                      className={`bi ${emailCheck.status === 'DUPLICATE_PRODUCT' ? 'bi-exclamation-triangle' : 'bi-info-circle'}`}
                      aria-hidden="true"
                    ></i>
                    <span>{emailCheck.message}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Provide Investor Details */}
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] mb-2">
            <div className="sa-introduce__section-head">
              <span>Provide Investor Details</span>
            </div>
            <div className="sa-introduce__grid">
              <div className="form-group">
                <label htmlFor="investorType">
                  Investor Type <span className="text-danger">*</span>
                </label>
                <SearchableSelect id="investorType" options={asOptions(investorTypes)}
                        value={form.investorTypeGuid} isInvalid={!!errors.investorTypeGuid}
                        disabled={isLocked('investorTypeGuid')}
                        onChange={v => set('investorTypeGuid', v)} />
                {err('investorTypeGuid')}{lockHint('investorTypeGuid')}
              </div>
              <div className="form-group">
                <label htmlFor="nationality">
                  Nationality <span className="text-danger">*</span>
                </label>
                <SearchableSelect id="nationality" options={asOptions(lookups?.nationalities, true)}
                        value={form.nationalityGuid} isInvalid={!!errors.nationalityGuid}
                        disabled={isLocked('nationalityGuid')}
                        onChange={v => set('nationalityGuid', v)} />
                {err('nationalityGuid')}{lockHint('nationalityGuid')}
              </div>
              <div className="form-group">
                <label htmlFor="residence">Country of Residence</label>
                <SearchableSelect id="residence" options={asOptions(lookups?.countriesOfResidence)}
                        value={form.countryOfResidenceGuid}
                        disabled={isLocked('countryOfResidenceGuid')}
                        onChange={v => set('countryOfResidenceGuid', v)} />
                {lockHint('countryOfResidenceGuid')}
              </div>
            </div>
          </div>

          {/* Investor Details */}
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] mb-2">
            <div className="sa-introduce__section-head">
              <span>Investor Details</span>
            </div>
            <div className="sa-introduce__grid">
              <div className="form-group">
                <label htmlFor="firstName">
                  First Name <span className="text-danger">*</span>
                </label>
                <input id="firstName" maxLength={100} className={cls('firstName')} value={form.firstName}
                       readOnly={isLocked('firstName')} disabled={isLocked('firstName')}
                       style={isLocked('firstName') ? lockStyle : undefined}
                       onChange={e => set('firstName', e.target.value)} />
                {err('firstName')}{lockHint('firstName')}
              </div>
              <div className="form-group">
                <label htmlFor="middleName">Middle Name</label>
                <input id="middleName" maxLength={100} className="form-control form-control-sm" value={form.middleName}
                       readOnly={isLocked('middleName')} disabled={isLocked('middleName')}
                       style={isLocked('middleName') ? lockStyle : undefined}
                       onChange={e => set('middleName', e.target.value)} />
                {lockHint('middleName')}
              </div>
              <div className="form-group">
                <label htmlFor="lastName">
                  Last Name <span className="text-danger">*</span>
                </label>
                <input id="lastName" maxLength={100} className={cls('lastName')} value={form.lastName}
                       readOnly={isLocked('lastName')} disabled={isLocked('lastName')}
                       style={isLocked('lastName') ? lockStyle : undefined}
                       onChange={e => set('lastName', e.target.value)} />
                {err('lastName')}{lockHint('lastName')}
              </div>

              <div className="form-group">
                <label htmlFor="isd">Country of Residence (ISD Code)</label>
                <SearchableSelect id="isd" options={asOptions(lookups?.isdCodes)}
                        value={form.isdCodeGuid} onChange={v => set('isdCodeGuid', v)} />
              </div>
              <div className="form-group">
                <label htmlFor="mobile">
                  Mobile Phone <span className="text-danger">*</span>
                </label>
                <input id="mobile" maxLength={15} inputMode="numeric" className={cls('mobile')} value={form.mobile}
                       onChange={e => set('mobile', e.target.value.replace(/[^0-9]/g, ''))} />
                {err('mobile')}
              </div>

              {/* Not shown: "Use Facilon Service Agent?" is always No, and the Client
                  Executive Agent is always the signed-in agent. Both are fixed by the
                  server from the agent's profile; the form sends the defaults. */}
            </div>
          </div>

          {/* Investor Product */}
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] mb-2">
            <div className="sa-introduce__section-head">
              <span>Investor Product</span>
            </div>
            <div className="sa-introduce__grid">
              <div className="form-group">
                <label htmlFor="route">
                  Investment Route <span className="text-danger">*</span>
                </label>
                <SearchableSelect id="route" options={asOptions(lookups?.investmentRoutes)}
                        value={form.investmentRouteGuid} isInvalid={!!errors.investmentRouteGuid}
                        onChange={v => set('investmentRouteGuid', v)} />
                {err('investmentRouteGuid')}
              </div>
              <div className="form-group form-group--wide">
                <label htmlFor="product">
                  Product <span className="text-danger">*</span>
                </label>
                <SearchableSelect id="product" options={asOptions(products)}
                        value={form.productGuid} isInvalid={!!errors.productGuid}
                        onChange={v => set('productGuid', v)} />
                {err('productGuid')}
                <div className="form-text text-[11px]">
                  {selectedType ? `Available to ${selectedType.name} investors at your firm.` : 'Products your firm offers.'}
                </div>
              </div>
              {!isPms && (
                <div className="form-group">
                  <label htmlFor="bank">
                    Broker Preferred Bank <span className="text-danger">*</span>
                  </label>
                  <SearchableSelect id="bank" options={asOptions(lookups?.brokerBanks)}
                          value={form.brokerBankGuid} isInvalid={!!errors.brokerBankGuid}
                          onChange={v => set('brokerBankGuid', v)} />
                  {err('brokerBankGuid')}
                </div>
              )}
              <div className="form-group form-group--wide">
                <label htmlFor="plan">
                  {isPms ? 'PMS Plan' : 'Brokerage Plan'} <span className="text-danger">*</span>
                </label>
                <SearchableSelect id="plan" options={asOptions(plans)} value={form.planGuid}
                        isInvalid={!!errors.planGuid}
                        onChange={v => set('planGuid', v)} />
                {err('planGuid')}
              </div>
              {isPms && (
                <>
                  <div className="form-group">
                    <label htmlFor="scheme">Scheme</label>
                    <input id="scheme" className="form-control form-control-sm" readOnly disabled
                           value={planDetail?.schemeName || (form.planGuid ? '—' : '')}
                           style={{ backgroundColor: '#e9ecef', color: '#495057' }} />
                    <div className="form-text text-[11px]">From the selected plan.</div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="pmsBank">Preferred Bank</label>
                    <input id="pmsBank" className="form-control form-control-sm" readOnly disabled
                           value={planDetail?.bankName || (form.planGuid ? '—' : '')}
                           style={{ backgroundColor: '#e9ecef', color: '#495057' }} />
                    <div className="form-text text-[11px]">From the selected plan.</div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] px-3 py-2 mb-2 d-flex align-items-center flex-wrap gap-2">
            <div className="text-[10.5px] me-auto" style={{ maxWidth: 520 }}
                 role={saving ? 'status' : undefined} aria-live={saving ? 'polite' : undefined}>
              {saving ? (
                <span className="text-[#2c5e6a] fw-semibold">
                  <span className="spinner-border spinner-border-sm me-2" style={{ width: 12, height: 12 }} aria-hidden="true"></span>
                  Creating the investor in Power Apps. This can take a few seconds while it sets up the contact and journey — please don&rsquo;t reload.
                </span>
              ) : (
                <span className="text-slate-500">
                  On save the investor record is created in Power Apps and the introduction email goes out within a minute.
                </span>
              )}
            </div>
            <button type="button" className="btn btn-sm btn-secondary" disabled={saving}
                    onClick={() => navigate('/service-agent/dashboard')}>
              Cancel
            </button>
            <button type="submit" className="btn btn-sm btn-primary"
                    disabled={saving || emailCheck?.status === 'DUPLICATE_PRODUCT'}>
              {saving ? 'Introducing…' : 'Introduce Investor'}
            </button>
          </div>
        </form>
        </>)}
      </main>
    </div>
  );
};

export default IntroduceInvestor;
